/**
 * `/tasks` — the Tasks section (`docs/13-tasks-section-proposal.md`, `specs/M10-tasks.md`).
 *
 * **Not monday.** Nothing on this page is parity work: monday has no Tasks section, so there is
 * no capture to copy and every number in `tasks.css` is ours, derived from the closest measured
 * surface the product already has — My Work (`discovery/ui-reference/my-work/01-anatomy.json`).
 * The deviations are listed in `tasks/T-T2.md`.
 *
 * Five tabs — **My Tasks · Tasks Assigned · Archive · Insights · Timeline**. The first three are
 * `tasks.list` over one materialised table; the last two are round **T3**'s panels, mounted here
 * by lazy import so this round neither owns nor blocks them.
 *
 * The page computes **no** grouping, no filtering and no permission decision of its own: the
 * server does all three (`services/tasks/queries.ts`), because the rule that an assignee sees a
 * task only on a board they can read has to be applied before anything is counted, and a browser
 * that grouped its own rows would be a second, disagreeing implementation of it.
 */
import { PageEmptyState, PageErrorState, useOptionalToast } from '@elizmonday/ui';
import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePhone } from '../board-view/table/phone/usePhoneLayout';
/*
 * **P-319** · the board's own item card, opened **over** this page.
 *
 * Imported from `modules/item-card` and not re-implemented: it is the same panel, the same four
 * tabs, the same ✕ and the same Escape, because *"si deve aprire la sidebar a destra ma non
 * cambiare pagina"* is a routing answer, not a second card. The module is imported by its files
 * rather than by its barrel (as `routes/screens.tsx` does) so the fixture source it also exports
 * stays out of the product bundle.
 */
import { ItemCard } from '../item-card/ItemCard';
import type { ItemCardSource } from '../item-card/types';
import { NudgeDialog } from './NudgeDialog';
import { TaskCellEditor, type TaskCellRole } from './inline-editors';
import { TaskFilters } from './TaskFilters';
import { TaskListHeader, TaskLegend, TaskSelectBox } from './TaskListHeader';
import { TaskRow } from './TaskRow';
import { TaskSettingsPanel } from './TaskSettingsPanel';
import { useTaskMappingPanel } from './useTaskMappingPanel';
import {
  applyArchiveUndo,
  applyBulkUndo,
  archiveTasksBulk,
  bulkStatusChoices,
  describeBulkOutcome,
  markTasksDone,
  setTasksDue,
  setTasksStatus,
  type BulkOutcome,
  type BulkStatusChoice,
} from './bulk';
import {
  EMPTY_TASK_SELECTION,
  describeTaskSelection,
  extendTaskRange,
  keepSelectedTasks,
  selectAllTasks,
  selectTasks,
  setTaskGroupSelected,
  taskSelectState,
  taskSelectionSet,
  toggleTask,
  visibleTaskOrder,
  type TaskSelection,
  type TaskSelectState,
} from './selection';
import {
  EMPTY_TASK_FILTERS,
  TASK_LIST_ERROR_HINT,
  TASK_PAGE_SIZE,
  activeFilterCount,
  describeListFailure,
  mergeTaskGroupPages,
  normaliseGroupBy,
  readSavedViews,
  removeSavedView,
  toGroupPageInput,
  toGroupedListInput,
  upsertSavedView,
  writeSavedViews,
  type TaskFilterState,
  type TaskSavedView,
} from './state';
import {
  TASK_GROUP_BY_FOR_TAB,
  TASK_GROUP_BY_LABELS,
  TASK_PAGE_TABS,
  TASK_SORT_LABELS,
  TASK_TAB_LABELS,
  panelTabOf,
} from './vocabulary';
import type { TasksInsightsSource } from './insights/source';
import { useColumnResize } from '../../lib/use-column-resize';
import type { TaskColumnKey } from './columns';
import {
  clampTaskColumnWidth,
  coerceTaskColumnWidths,
  taskColumnWidthStyle,
  taskColumnWidthsFor,
  taskColumnWidthsToView,
  withTaskColumnWidth,
  withoutTaskColumnWidth,
  type TaskColumnWidthsByTab,
} from './column-widths';
import {
  TASK_SORTS,
  type TaskCountsView,
  type TaskFacetsView,
  type TaskGroupBy,
  type TaskGroupPageView,
  type TaskItemRef,
  type TaskListByGroupResultView,
  type TaskMappingView,
  type TaskPageTab,
  type TaskRowView,
  type TaskSettingsView,
  type TaskSort,
  type TaskTab,
  type TasksSource,
} from './types';

/*
 * Round **T3** owns `./insights/**` and `./timeline/**`. They are lazy on purpose and not only
 * for the bundle: the two folders are another worker's, and a static import would make this
 * round's build depend on the moment theirs lands. `Suspense` covers the gap; a panel that has
 * not shipped shows its own placeholder.
 */
/**
 * How many rows one turn of the list asks for, and how much "Show more" adds (P-273/P-277).
 * It lives in `state.ts` with the rest of the list state; re-exported because round T8 named it
 * here and the verifier and the tests import it from here.
 */
export const TASKS_PAGE_SIZE = TASK_PAGE_SIZE;

const InsightsPanel = lazy(() =>
  import('./insights/InsightsPanel').then((module) => ({ default: module.InsightsPanel })),
);
const TimelinePanel = lazy(() =>
  import('./timeline/TimelinePanel').then((module) => ({ default: module.TimelinePanel })),
);

export interface TasksPageProps {
  source: TasksSource;
  /**
   * Round **T4**. The two panels build their own tRPC source when they are given none (T3's
   * `source?`); the fixture harness hands them one instead, so all five tabs can be rendered —
   * and screenshotted on a phone — with no api and no database.
   */
  insightsSource?: TasksInsightsSource;
  /** Which tab the route is on. The page never navigates itself. */
  tab: TaskPageTab;
  onTabChange(tab: TaskPageTab): void;
  /**
   * Opens the item card. Injected, so the page works in the fixture harness with no router.
   *
   * **P-319** widened the parameter from `TaskRowView` to the two ids every caller actually uses,
   * so the Timeline panel's `TaskMark` can go through the same handler as a list row.
   */
  onOpenItem?(ref: TaskItemRef): void;
  /**
   * **P-319** · the item whose card is open **over** this page, from the URL
   * (`/tasks/pulses/:itemId`), or `null`.
   *
   * The page does not own it: it is a route segment, exactly as it is on the board
   * (`routes/index.tsx` R3), so a reload reopens the card with the list mounted underneath and
   * Back closes it. What the page owns is the paint — the panel, the row's `data-open`, and a
   * re-read when it closes.
   */
  openItemId?: string | null;
  /**
   * The card's data source. The product route hands over the tRPC one it already builds for the
   * board (`useItemCardSource`), the `/__ui/tasks` harness hands over the fixture one; `null` or
   * absent means no card is drawn at all, which is how the jsdom units that predate this round
   * keep rendering unchanged.
   */
  itemCardSource?: ItemCardSource | null;
  /** The ✕, Escape and Back: the route returns to the tab's own URL. */
  onCloseItem?(): void;
  /** The card header's `Open board ↗` — leaving the Tasks page, on purpose. */
  onOpenBoard?(ref: TaskItemRef): void;
  /** Bumped by `task.changed`; every bump re-reads the list and the counts. */
  revision?: number;
}

/** The list tab a panel reports on: Insights and Timeline take `mine | assigned`, never archive. */
function listTabOf(tab: TaskPageTab): TaskTab {
  return tab === 'assigned' ? 'assigned' : tab === 'archive' ? 'archive' : 'mine';
}

export function TasksPage({
  source,
  insightsSource,
  tab,
  onTabChange,
  onOpenItem,
  openItemId = null,
  itemCardSource = null,
  onCloseItem,
  onOpenBoard,
  revision = 0,
}: TasksPageProps) {
  const toast = useOptionalToast();

  /**
   * The list, paged **per group** — **P-318**.
   *
   * Until this round the window was a list of flat pages walked with one cursor (P-277), so the
   * first 50 rows of a 638-row tab were 50 rows of *This week* and nothing at all under *No date ·
   * 583*: the group header counted rows the page had no way to show. `tasks.listByGroup` answers
   * **every** group of the tab with its own first page and its own cursor, and `tasks.listGroup`
   * continues one of them — so `grouped` is that answer and `extraPages` is what each group's own
   * "Show more" has added, keyed by group.
   *
   * `mergeTaskGroupPages` joins the two. The browser still computes **no** grouping: the groups,
   * their order, their labels and their counts are all the server's (P-271).
   */
  const [grouped, setGrouped] = useState<TaskListByGroupResultView | null>(null);
  const [extraPages, setExtraPages] = useState<Record<string, TaskGroupPageView[]>>({});
  const [counts, setCounts] = useState<TaskCountsView | null>(null);
  const [settings, setSettings] = useState<TaskSettingsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState<TaskFilterState>({ ...EMPTY_TASK_FILTERS });
  const [sort, setSort] = useState<TaskSort>('due_asc');
  const [groupBy, setGroupBy] = useState<TaskGroupBy>('due');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  /**
   * P-285 · which tasks are ticked, and where a Shift range measures from.
   *
   * It was a `Record<string, boolean>` until this round; the model lives in `selection.ts` now
   * because a range, a tri-state header and a per-group box are three questions a bag of
   * booleans cannot answer the same way twice.
   */
  const [selection, setSelection] = useState<TaskSelection>(EMPTY_TASK_SELECTION);
  /** True while "Select all N in this tab" is walking every group's remaining pages. */
  const [selectingTab, setSelectingTab] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savedViews, setSavedViews] = useState<TaskSavedView[]>([]);
  const [facets, setFacets] = useState<TaskFacetsView | null>(null);
  /** Which groups have a "Show more" in flight — P-318, one per group, never one for the list. */
  const [loadingGroups, setLoadingGroups] = useState<Record<string, boolean>>({});
  /**
   * How many pages each **group** had open, so a reload (a cell edit, a bulk action, a realtime
   * `task.changed`) puts the list back where the person left it. A reload that dropped every
   * group back to its first page would throw away everything they had expanded each time a row
   * changed; it is a ref rather than state because the loader reads it and must not re-run when
   * it moves.
   */
  const pagesWanted = useRef<Record<string, number>>({});
  const [bulkDue, setBulkDue] = useState('');
  /**
   * **P-301 §2** · what *Set status ▾* may offer for the current selection.
   *
   * It is resolved from the servers the selection touches — each board's mapping and that
   * column's own labels — rather than from the rows on screen, because a row carries the label
   * it *has* and the menu must offer the ones it could be given. `null` while it is in flight,
   * which is what the disabled state reads.
   */
  const [statusChoices, setStatusChoices] = useState<BulkStatusChoice[] | null>(null);
  const [editor, setEditor] = useState<{ row: TaskRowView; role: TaskCellRole } | null>(null);
  const [editorMapping, setEditorMapping] = useState<TaskMappingView | null>(null);
  const [nudging, setNudging] = useState<TaskRowView | null>(null);
  /**
   * Round T4 · bulk select on a phone is **off** until a long press turns it on (F9-D32).
   *
   * A checkbox in every row is 44 px of a 390 px screen spent on a gesture nobody performs most
   * of the time, and it is the first thing the user's report names ("giant checkboxes"). The
   * column is therefore hidden below the phone breakpoint until the list is *in* select mode,
   * exactly as a phone mail client does it. On a desktop nothing changes: `usePhone()` is false,
   * no row gets a long-press handler, and the column is never hidden.
   */
  const phone = usePhone();
  const [selectMode, setSelectMode] = useState(false);
  /** Local re-read trigger: a write asks for the list again without touching the route. */
  const [localRevision, setLocalRevision] = useState(0);

  const isList = tab === 'mine' || tab === 'assigned' || tab === 'archive';
  const listTab = listTabOf(tab);
  /**
   * Which list the two panels report on.
   *
   * It is **not** derivable from the route: `/tasks/insights` is one URL and `docs/13` §3
   * describes two sets of numbers behind it, a *Delegated* one and a *Personal* one. Deriving it
   * from `tab` made `listTabOf('insights')` answer `'mine'` and put the delegated half out of
   * reach. So the subject is its own piece of state: it opens on the **list tab the person came
   * from**, it survives a reload through the `tasks.tab` preference, and inside the panel T3's
   * own `SubjectSwitch` lets them read the other half without leaving it.
   */
  const [panelSubject, setPanelSubject] = useState<'mine' | 'assigned'>('mine');
  /** `false` until `tasks.settings.get` has answered; nothing is written back before then. */
  const settingsLoaded = useRef(false);
  const lastSavedTab = useRef<TaskTab | null>(null);

  /**
   * **P-305** · this user's column widths, per tab (`preferences` key `tasks.columnWidths`).
   *
   * Held here and not in `TaskListHeader` because the widths are custom properties on the whole
   * list: the strip and every row read the same `--ez-tasks-*-w`, which is the only reason a
   * flex header can sit over a flex row at all (P-270). A header that owned them could style
   * itself and nothing else.
   */
  const [columnWidths, setColumnWidths] = useState<TaskColumnWidthsByTab>({});

  useEffect(() => {
    let cancelled = false;
    source
      .columnWidths()
      .then((stored) => {
        if (cancelled) return;
        setColumnWidths(coerceTaskColumnWidths(stored));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [source]);

  /**
   * One write, for the drag's pointer-up, the keyboard's arrow and the reset alike.
   *
   * The page's copy is updated first and the write is not waited on: a width is a paint, and a
   * list that froze until `preferences.setMany` answered would make a resize feel like a save.
   */
  const commitColumnWidths = useCallback(
    (next: TaskColumnWidthsByTab) => {
      setColumnWidths(next);
      void source
        .saveColumnWidths(taskColumnWidthsToView(next))
        .then((written) => setColumnWidths(coerceTaskColumnWidths(written)))
        .catch(() => undefined);
    },
    [source],
  );

  /**
   * The drag itself — the board grid's gesture, out of `lib/use-column-resize.ts`: pointer
   * capture, a live preview that paints and never writes, and one commit on pointer-up.
   */
  const resize = useColumnResize<TaskColumnKey>({
    clamp: clampTaskColumnWidth,
    onCommit: (key, width) => commitColumnWidths(withTaskColumnWidth(columnWidths, listTab, key, width)),
  });

  /** The stored widths with the drag in flight laid over them: what the list is painted at. */
  const widthsForTab = useMemo(() => {
    const stored = taskColumnWidthsFor(columnWidths, listTab);
    return resize.active === null ? stored : { ...stored, [resize.active.key]: resize.active.width };
  }, [columnWidths, listTab, resize.active]);

  /*
   * **Phone (≤767) is untouched**, which is the card's own word: the two-line card has no
   * columns to widen, the strip that carries the gutters is not drawn, and a width written on a
   * desktop must not reach the properties `mobile.css` re-declares for the card. So the style is
   * simply not applied there — the handles are not in the DOM either (`resizable`).
   */
  const columnStyle = phone ? undefined : taskColumnWidthStyle(widthsForTab);
  const resizedColumns = useMemo(
    () => Object.keys(taskColumnWidthsFor(columnWidths, listTab)) as TaskColumnKey[],
    [columnWidths, listTab],
  );

  useEffect(() => {
    setSavedViews(readSavedViews());
  }, []);

  /* The stored preferences decide the first grouping and the first "show archived" (§5). */
  useEffect(() => {
    let cancelled = false;
    source
      .settings()
      .then((next) => {
        if (cancelled) return;
        setSettings(next);
        setGroupBy(normaliseGroupBy(listTab, next.groupBy));
        setFilters((current) => ({ ...current, includeArchived: next.showArchived }));
        /*
         * A deep link straight to `/tasks/insights` has no list tab to come from, so the stored
         * `tasks.tab` is what decides — which is exactly what that key is for (§5).
         */
        if (tab === 'insights' || tab === 'timeline') {
          setPanelSubject(next.tab === 'assigned' ? 'assigned' : 'mine');
        }
        lastSavedTab.current = next.tab;
        settingsLoaded.current = true;
        /*
         * The route wins over the stored tab when the two disagree — somebody who followed a link
         * to `/tasks/assigned` is on Tasks Assigned, and the preference should say so from now on.
         */
        if ((tab === 'mine' || tab === 'assigned' || tab === 'archive') && next.tab !== tab) {
          lastSavedTab.current = tab;
          void source.saveSettings({ tab }).catch(() => undefined);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Only on mount: afterwards the page is the authority on its own switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  /** A grouping the tab does not offer is corrected rather than sent. */
  useEffect(() => {
    setGroupBy((current) => normaliseGroupBy(listTab, current));
    setSelection(EMPTY_TASK_SELECTION);
    setSelectMode(false);
  }, [listTab]);

  /**
   * A different question is a new list: never keep the previous one's window.
   *
   * Declared **before** the loader, so on a question change the ref is already 1 by the time it
   * runs, while a plain reload (which does not re-run this one) keeps the loaded pages.
   */
  useEffect(() => {
    pagesWanted.current = {};
  }, [listTab, groupBy, sort, filters]);

  /**
   * The filter bar's options — the tab's **unfiltered** row set (P-274). It depends on the tab
   * and on "show archived" and on nothing else, so choosing a filter never removes its own
   * alternatives from the picker that offered it.
   */
  useEffect(() => {
    if (!isList) return;
    let cancelled = false;
    source
      .facets({ tab: listTab, includeArchived: filters.includeArchived })
      .then((next) => {
        if (!cancelled) setFacets(next);
      })
      .catch(() => {
        if (!cancelled) setFacets(null);
      });
    return () => {
      cancelled = true;
    };
  }, [source, isList, listTab, filters.includeArchived, revision, localRevision]);

  /**
   * Standing on a list tab both **arms the panels' subject** and remembers the tab, so that
   * opening Insights from Tasks Assigned reports on the delegations rather than silently
   * switching to the personal numbers.
   */
  useEffect(() => {
    if (tab !== 'mine' && tab !== 'assigned' && tab !== 'archive') return;
    if (tab === 'mine' || tab === 'assigned') setPanelSubject(tab);
    if (!settingsLoaded.current || lastSavedTab.current === tab) return;
    lastSavedTab.current = tab;
    setSettings((current) => (current ? { ...current, tab } : current));
    void source.saveSettings({ tab }).catch(() => undefined);
  }, [tab, source]);

  const reload = useCallback(() => setLocalRevision((value) => value + 1), []);

  useEffect(() => {
    if (!isList) return;
    let cancelled = false;
    setLoading(true);
    /**
     * Every group's first page, plus — per group — as many more as were open before the reload.
     *
     * The continuations are walked group by group so that a reload restores exactly what each
     * group had expanded: a person who pressed "Show more" four times under *No date* and never
     * touched *Later* gets four pages of *No date* and one of *Later* back, not four of each.
     */
    const readWindow = async (): Promise<{
      first: TaskListByGroupResultView;
      extra: Record<string, TaskGroupPageView[]>;
    }> => {
      const query = { tab: listTab, groupBy, sort, filters, limit: TASK_PAGE_SIZE };
      const first = await source.listByGroup(toGroupedListInput(query));
      const extra: Record<string, TaskGroupPageView[]> = {};
      if (cancelled) return { first, extra };
      for (const group of first.groups) {
        const wanted = pagesWanted.current[group.key] ?? 1;
        let cursor = group.nextCursor;
        for (let turn = 1; turn < wanted && cursor !== null; turn += 1) {
          const next = await source.listGroup(toGroupPageInput(query, group.key, cursor));
          if (cancelled) return { first, extra };
          extra[group.key] = [...(extra[group.key] ?? []), next];
          cursor = next.nextCursor;
        }
      }
      return { first, extra };
    };
    Promise.all([readWindow(), source.counts()])
      .then(([loaded, nextCounts]) => {
        if (cancelled) return;
        setGrouped(loaded.first);
        setExtraPages(loaded.extra);
        pagesWanted.current = Object.fromEntries(
          loaded.first.groups.map((group) => [
            group.key,
            (loaded.extra[group.key]?.length ?? 0) + 1,
          ]),
        );
        setCounts(nextCounts);
        setSettings(loaded.first.settings);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        /* P-277: a sentence on the page, the technical text in the console. */
        console.error('tasks.listByGroup failed', describeListFailure(cause));
        setError(TASK_LIST_ERROR_HINT);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [source, isList, listTab, groupBy, sort, filters, revision, localRevision]);

  /* The counts feed the tab labels on every tab, including the two panels' (`docs/13` §2). */
  useEffect(() => {
    if (isList) return;
    let cancelled = false;
    source
      .counts()
      .then((next) => {
        if (!cancelled) setCounts(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [source, isList, revision]);

  const loadedWindow = useMemo(() => mergeTaskGroupPages(grouped, extraPages), [grouped, extraPages]);
  const rows = loadedWindow.rows;
  const groups = loadedWindow.groups;
  /** group key → that group's own cursor; `null` means the group is fully loaded (P-318). */
  const cursors = loadedWindow.cursors;
  /** The base as it is *now*, for an await that has to know whether it moved (P-285). */
  const groupedRef = useRef(grouped);
  useEffect(() => {
    groupedRef.current = grouped;
  }, [grouped]);

  /**
   * One more page of **one group** — **P-318**, the board grid's own gesture
   * (`board-view/table/store.ts#loadMore`: a cursor per group, rows appended under that group).
   *
   * The append is discarded if the base reloaded while it was in flight, so a realtime repaint can
   * never interleave a page of a list that is gone.
   */
  const loadMoreGroup = useCallback(
    (groupKey: string) => {
      const cursor = cursors[groupKey];
      if (cursor === null || cursor === undefined || loadingGroups[groupKey] === true) return;
      const base = grouped;
      setLoadingGroups((current) => ({ ...current, [groupKey]: true }));
      source
        .listGroup(
          toGroupPageInput(
            { tab: listTab, groupBy, sort, filters, limit: TASK_PAGE_SIZE },
            groupKey,
            cursor,
          ),
        )
        .then((next) => {
          if (groupedRef.current !== base) return;
          setExtraPages((current) => {
            const grown = [...(current[groupKey] ?? []), next];
            pagesWanted.current[groupKey] = grown.length + 1;
            return { ...current, [groupKey]: grown };
          });
        })
        .catch((cause: unknown) => {
          console.error('tasks.listGroup (show more) failed', describeListFailure(cause));
          setError(TASK_LIST_ERROR_HINT);
        })
        .finally(() => setLoadingGroups((current) => ({ ...current, [groupKey]: false })));
    },
    [cursors, loadingGroups, grouped, source, listTab, groupBy, sort, filters],
  );

  /* ── P-285 · the selection, derived ────────────────────────────────────── */

  /** Every row of every page walked so far — what "Select all" and the header box mean. */
  const loadedIds = useMemo(() => rows.map((row) => row.id), [rows]);
  /** The rows actually painted, in paint order — what a Shift range runs along. */
  const order = useMemo(() => visibleTaskOrder(groups, collapsed), [groups, collapsed]);
  const chosen = useMemo(() => taskSelectionSet(selection), [selection]);
  const selectedRows = useMemo(() => rows.filter((row) => chosen.has(row.id)), [rows, chosen]);
  const headerState = useMemo(() => taskSelectState(selection, loadedIds), [selection, loadedIds]);
  /**
   * How many rows the **whole tab** holds under the current filters — the M of "Select all M in
   * this tab". It is the list's own `total` (the server counts the filtered tab on every page),
   * not `tasks.counts`, which counts the tab unfiltered and would offer to select rows the
   * filters have already excluded.
   */
  const tabTotal = loadedWindow.total;
  /** True while **any** group still has a page left to fetch (P-318). */
  const hasMorePages =
    Object.values(cursors).some((cursor) => cursor !== null) && tabTotal > rows.length;

  /* ── P-319 · the item card, open over the list ─────────────────────────── */

  /**
   * The row the open card is about, when the list carries it.
   *
   * It is what `Open board ↗` needs: the URL names the **item** (monday's own `pulses/<id>` deep
   * link, which carries no board), and the board is a fact about the row. A card deep-linked to an
   * item that is not in this tab's loaded pages therefore offers no board link rather than a link
   * to a guessed board — the one conservative reading (CLAUDE.md rule 3).
   */
  const openRow = useMemo(
    () => (openItemId === null ? null : (rows.find((row) => row.itemId === openItemId) ?? null)),
    [rows, openItemId],
  );

  const openItemCard = useCallback(
    (ref: TaskItemRef) => onOpenItem?.({ itemId: ref.itemId, boardId: ref.boardId }),
    [onOpenItem],
  );

  /**
   * Closing the card re-reads the list.
   *
   * The live path is realtime (`useTasksRealtime`, which holds the open card's board room for
   * exactly this reason), and this is the floor under it: a rename made in the card with the
   * websocket blocked, or on a deep link whose board the page never learned, still lands on the
   * row the moment the panel comes down. One read, once, on the close.
   */
  const hadCardOpen = useRef(openItemId !== null);
  useEffect(() => {
    const open = openItemId !== null;
    const closed = hadCardOpen.current && !open;
    hadCardOpen.current = open;
    if (closed) reload();
  }, [openItemId, reload]);

  /**
   * A row the list no longer carries leaves the selection (the card: *"selection survives sort
   * and group changes for rows still present"*). `keepSelectedTasks` returns the **same object**
   * when nothing changed, so this effect cannot loop on its own output.
   */
  useEffect(() => {
    setSelection((current) => keepSelectedTasks(current, loadedIds));
  }, [loadedIds]);

  const toggleRow = useCallback(
    (rowId: string, modifiers: { shift: boolean }) => {
      setSelectMode(true);
      setSelection((current) =>
        modifiers.shift
          ? extendTaskRange(current, order, rowId)
          : toggleTask(current, order, rowId),
      );
    },
    [order],
  );

  const selectAllLoaded = useCallback(() => {
    setSelectMode(true);
    setSelection(selectAllTasks(loadedIds));
  }, [loadedIds]);

  const clearSelection = useCallback(() => {
    setSelection(EMPTY_TASK_SELECTION);
    setSelectMode(false);
  }, []);

  /**
   * **Select all M in this tab** — the other half of the gesture, for a tab longer than the pages
   * on screen. P-318: it now walks **every group's** remaining pages, not one flat cursor.
   *
   * It keeps the pages it walked, so the rows it selects are rows the list *has*: the bulk bar's
   * count, the group counts and what an action writes all stay the same list. Appending through
   * `mergeTaskGroupPages` is what makes that safe — the browser still computes no grouping, it
   * concatenates the server's own partitions.
   *
   * The walk is abandoned if the base list reloaded underneath it (a realtime repaint, an edit),
   * exactly as `loadMoreGroup` abandons a stale append.
   */
  const selectWholeTab = useCallback(() => {
    if (selectingTab) return;
    const base = grouped;
    if (!base) return;
    const pending = Object.entries(cursors).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    );
    if (pending.length === 0) return;
    setSelectingTab(true);
    void (async () => {
      const grown: Record<string, TaskGroupPageView[]> = { ...extraPages };
      const query = { tab: listTab, groupBy, sort, filters, limit: TASK_PAGE_SIZE };
      try {
        for (const [groupKey, from] of pending) {
          let cursor: string | null = from;
          /* A group of 500 pages is a list nobody can act on; the guard is a stuck-cursor stop. */
          for (let turn = 0; turn < 500 && cursor !== null; turn += 1) {
            const next: TaskGroupPageView = await source.listGroup(
              toGroupPageInput(query, groupKey, cursor),
            );
            grown[groupKey] = [...(grown[groupKey] ?? []), next];
            cursor = next.nextCursor;
          }
        }
      } catch (cause: unknown) {
        console.error('tasks.listGroup (select all) failed', describeListFailure(cause));
        setError(TASK_LIST_ERROR_HINT);
        setSelectingTab(false);
        return;
      }
      setSelectingTab(false);
      /* The base reloaded while we were walking: those pages describe a list that is gone. */
      if (groupedRef.current !== base) return;
      const ids = mergeTaskGroupPages(base, grown).rows.map((row) => row.id);
      pagesWanted.current = Object.fromEntries(
        base.groups.map((group) => [group.key, (grown[group.key]?.length ?? 0) + 1]),
      );
      setExtraPages(grown);
      setSelectMode(true);
      setSelection(selectTasks(ids, ids));
    })();
  }, [selectingTab, grouped, extraPages, cursors, source, listTab, groupBy, sort, filters]);

  /**
   * `Ctrl/Cmd+A` selects every loaded row, `Esc` clears — both only while the focus is **in the
   * list** (P-285).
   *
   * It is a document listener rather than a handler on the list, because `Ctrl+A`'s default is
   * the browser's *select the whole page* and only a listener that sees the event before the
   * document can prevent it. The containment test is what keeps `Ctrl+A` in the search box
   * selecting the search box's text, which is what a person means there.
   */
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isList) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      const inList = target !== null && listRef.current !== null && listRef.current.contains(target);
      if (event.key === 'Escape') {
        /* An open editor, dialog or sheet owns Escape first — it is closing itself. */
        if (editor !== null || nudging !== null || settingsOpen || filtersOpen) return;
        /*
         * **P-319 · one layer at a time** (F4 §P5, P-167). The item card is above the list and
         * closes itself on Escape; without this line the one keypress closed the card *and*
         * emptied the selection, so coming back from a card cost the person their ticks — the
         * very list state the card exists to preserve.
         */
        if (openItemId !== null) return;
        if (selection.ids.length === 0) return;
        clearSelection();
        return;
      }
      if ((event.key === 'a' || event.key === 'A') && (event.ctrlKey || event.metaKey)) {
        if (!inList || event.altKey) return;
        event.preventDefault();
        selectAllLoaded();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    isList,
    editor,
    nudging,
    settingsOpen,
    filtersOpen,
    openItemId,
    selection.ids.length,
    clearSelection,
    selectAllLoaded,
  ]);

  /**
   * What every bulk action does when it comes back: clear the selection, re-read the list, and
   * offer **Undo** when the action captured enough to put the rows back (`bulk.ts`).
   */
  const afterBulk = useCallback(
    (outcome: BulkOutcome, undoLabel: string, verb = 'updated') => {
      clearSelection();
      reload();
      /*
       * P-301 · two shapes of undo, one offer. A cell action puts its rows back through
       * `cells.bulkSet` (P-285); archive and unarchive write no cell at all, so theirs is the
       * opposite `tasks.archive` call over exactly what moved. Whichever it is, the person sees
       * one **Undo** and one sentence.
       */
      const undoCells = outcome.undo.length > 0;
      const undoArchive = outcome.undoArchive;
      const runUndo = () => {
        const work = undoCells
          ? applyBulkUndo(source, outcome.undo)
          : applyArchiveUndo(source, undoArchive!);
        void work
          .then((written) => {
            reload();
            toast(`${undoLabel} undone — ${written} restored.`, { kind: 'positive' });
          })
          .catch(() => toast('That could not be undone.', { kind: 'negative' }));
      };
      toast(describeBulkOutcome(outcome, 'task', verb), {
        kind: outcome.skipped.length > 0 ? 'warning' : 'positive',
        ...(undoCells || undoArchive !== undefined
          ? { action: { label: 'Undo', onClick: runUndo } }
          : {}),
      });
    },
    [clearSelection, reload, toast, source],
  );

  /**
   * P-301 §2 · the labels *Set status ▾* may offer, re-resolved whenever the boards under the
   * selection change.
   *
   * Keyed on the **set of boards** and not on the selection itself: ticking a fourth row on a
   * board already in the selection changes nothing about what can be written, and re-reading two
   * mappings per tick would be one round trip per checkbox.
   */
  const selectedBoardKey = useMemo(
    () => [...new Set(selectedRows.map((row) => row.boardId))].sort().join(','),
    [selectedRows],
  );
  useEffect(() => {
    if (selectedBoardKey === '') {
      setStatusChoices(null);
      return;
    }
    let cancelled = false;
    setStatusChoices(null);
    void bulkStatusChoices(source, selectedRows)
      .then((resolved) => {
        if (!cancelled) setStatusChoices(resolved.choices);
      })
      .catch(() => {
        if (!cancelled) setStatusChoices([]);
      });
    return () => {
      cancelled = true;
    };
    /* `selectedRows` is read, but the **boards** are what decide the answer — see above. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, selectedBoardKey]);

  /**
   * The floor under `afterBulk`: a bulk action skips and continues per board (P-293), so the
   * only way to land here is something no board owns — the network, a bug. Even then the list
   * is re-read and the person is told, because the 2026-09-17 report was written by somebody
   * looking at a list that had not moved.
   */
  const bulkFailed = useCallback(() => {
    clearSelection();
    reload();
    toast('That could not be completed. Nothing was changed — please try again.', { kind: 'negative' });
  }, [clearSelection, reload, toast]);

  const saveSettings = useCallback(
    (patch: Partial<TaskSettingsView>) => {
      setSettings((current) => (current ? { ...current, ...patch } : current));
      void source
        .saveSettings(patch)
        .then((next) => setSettings(next))
        .then(reload)
        .catch(() => undefined);
    },
    [source, reload],
  );

  /**
   * **P-294** — Preferences ▸ Board mapping. The boards are the facets' own (the boards this
   * person's tasks are on), nothing is read until the pane is open, and a change re-reads the
   * list because the mapping is what the buckets are derived from.
   */
  const mappingPanel = useTaskMappingPanel(
    source,
    facets?.boards.map((board) => ({ id: board.id, name: board.name })) ?? [],
    reload,
    settingsOpen,
  );

  const openEditor = useCallback(
    (row: TaskRowView, role: TaskCellRole) => {
      setEditorMapping(null);
      setEditor({ row, role });
      void source
        .mapping({ boardId: row.boardId })
        .then(setEditorMapping)
        .catch(() => {
          setEditor(null);
          toast('That board could not be read.', { kind: 'negative' });
        });
    },
    [source, toast],
  );

  const countFor = (entry: TaskPageTab): number | null => {
    if (!counts) return null;
    if (entry === 'mine') return counts.mine;
    if (entry === 'assigned') return counts.assigned;
    if (entry === 'archive') return counts.archive;
    return null;
  };

  const editorNode =
    editor && editorMapping ? (
      <TaskCellEditor
        source={source}
        target={editor}
        mapping={editorMapping}
        onWritten={() => {
          reload();
          toast('Task updated.', { kind: 'positive' });
        }}
        onClose={() => {
          setEditor(null);
          setEditorMapping(null);
        }}
        renderUnavailable={(reason) => (
          <button
            type="button"
            className="ez-tasks__editor-unavailable"
            data-testid="tasks-editor-unavailable"
            onClick={() => {
              const row = editor.row;
              setEditor(null);
              setEditorMapping(null);
              openItemCard(row);
            }}
            title={reason}
          >
            Open the item
          </button>
        )}
      />
    ) : editor ? (
      <span className="ez-tasks__editor-loading" role="status" data-testid="tasks-editor-loading">
        Loading…
      </span>
    ) : null;

  /**
   * **P-319** · the card, drawn as a sibling of the page and not inside it.
   *
   * `.ez-item-card` is a `position: fixed` 700 px panel at the viewport's right edge, so it is
   * mounted beside `.ez-tasks` exactly as it is beside the board on `routes/screens.tsx` — the
   * page's own box, its gutters and its six column widths are the measured ones whether the card
   * is up or not, and the list keeps its scroll because nothing about its layout moved. It
   * **covers** the list's right-hand columns while it is open, which is what it does over the
   * grid too; only the board activity log reserves its width (P-016).
   *
   * `Open board ↗` cannot live inside the card — `ItemCard`'s props are `source / itemId /
   * initialTab / onClose` and the card is another round's module — so it is a sibling pinned into
   * the card's own 24 px top padding, above the ✕ / name / ⋯ line (`tasks.css`). That is the one
   * band of the header that is guaranteed empty at every width.
   */
  const cardNode =
    openItemId !== null && itemCardSource !== null ? (
      <div className="ez-tasks__card-host" data-testid="tasks-card-host">
        <ItemCard
          source={itemCardSource}
          itemId={openItemId}
          onClose={() => onCloseItem?.()}
        />
        {openRow !== null && onOpenBoard !== undefined ? (
          <button
            type="button"
            className="ez-tasks__card-board"
            data-testid="tasks-card-open-board"
            title={`Open ${openRow.boardName}`}
            onClick={() => onOpenBoard({ itemId: openRow.itemId, boardId: openRow.boardId })}
          >
            Open board <span aria-hidden="true">↗</span>
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <>
    <div
      className="ez-tasks"
      data-selectmode={selectMode || undefined}
      /*
       * P-319 · so the page can say out loud that a card is over it (and style for it). It
       * follows the **painted** card, not the URL: between a deep link and the moment the route
       * has a source to hand over there is nothing on screen to be "over" the list.
       */
      data-card-open={cardNode !== null || undefined}
      data-testid="tasks-page"
    >
      <header className="ez-tasks__header">
        <h1 className="ez-tasks__title" data-testid="tasks-title">
          Tasks
        </h1>
        <button
          type="button"
          className="ez-tasks__settings-open"
          data-testid="tasks-settings-open"
          onClick={() => setSettingsOpen(true)}
        >
          Preferences
        </button>
      </header>

      <div className="ez-tasks__tabs" role="tablist" aria-label="Tasks views" data-testid="tasks-tabs">
        {TASK_PAGE_TABS.map((entry) => {
          const count = countFor(entry);
          return (
            <button
              key={entry}
              type="button"
              role="tab"
              aria-selected={tab === entry}
              className={`ez-tasks__tab${tab === entry ? ' is-selected' : ''}`}
              data-testid={`tasks-tab-${entry}`}
              onClick={() => onTabChange(entry)}
            >
              {TASK_TAB_LABELS[entry]}
              {count === null ? null : (
                <span className="ez-tasks__tab-count" data-testid={`tasks-tab-count-${entry}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/*
        P-270 · the phone's legend. Above 767 px `tasks.css` hides it and the sticky column
        header below carries the same words; below it, it is the only line that says what the
        avatar and the two chips in a card row mean.
      */}
      {isList ? <TaskLegend tab={listTab} /> : null}

      {isList ? (
        <TasksToolbar
          listTab={listTab}
          groupBy={groupBy}
          sort={sort}
          filters={filters}
          savedViews={savedViews}
          filtersOpen={filtersOpen}
          selectState={headerState}
          onSelectAll={selectAllLoaded}
          onToggleFilters={() => setFiltersOpen((open) => !open)}
          onGroupBy={(next) => {
            setGroupBy(next);
            saveSettings({ groupBy: next });
          }}
          onSort={setSort}
          onQuery={(query) => setFilters((current) => ({ ...current, query }))}
          onApplyView={(view) => {
            const nextGroupBy = normaliseGroupBy(listTab, view.groupBy);
            setFilters(view.filters);
            setSort(view.sort);
            setGroupBy(nextGroupBy);
            /*
             * A view restores the *whole* state, and two thirds of that state are stored
             * preferences — so applying one writes them back, exactly as picking the same
             * grouping or the same toggle by hand would. Without this, a reload after applying a
             * view put the old grouping and the old "show archived" back (P-275).
             */
            saveSettings({ groupBy: nextGroupBy, showArchived: view.filters.includeArchived });
          }}
          onDeleteView={(id) => {
            const next = removeSavedView(savedViews, id);
            setSavedViews(next);
            writeSavedViews(next);
          }}
        />
      ) : null}

      {isList && filtersOpen ? (
        <TaskFilters
          tab={listTab}
          filters={filters}
          rows={rows}
          facets={facets}
          asSheet
          onClose={() => setFiltersOpen(false)}
          onChange={(next) => {
            setFilters(next);
            if (next.includeArchived !== filters.includeArchived) {
              saveSettings({ showArchived: next.includeArchived });
            }
          }}
          onSaveView={() => {
            const name = window.prompt('Name this view');
            if (name === null || name.trim() === '') return;
            const next = upsertSavedView(savedViews, {
              id: `${Date.now()}`,
              name: name.trim(),
              tab: listTab,
              groupBy,
              sort,
              filters,
            });
            setSavedViews(next);
            writeSavedViews(next);
            toast(`Saved "${name.trim()}".`, { kind: 'positive' });
          }}
        />
      ) : null}

      {tab === 'archive' ? (
        <div className="ez-tasks__archive-note" data-testid="tasks-archive-note">
          Done tasks move here {settings ? settings.archiveDelayDays : 14} days after they were
          completed. Reopening one takes it straight back out.
          <button
            type="button"
            className="ez-tasks__archive-delay"
            data-testid="tasks-archive-delay"
            onClick={() => setSettingsOpen(true)}
          >
            Change the delay
          </button>
        </div>
      ) : null}

      {tab === 'insights' ? (
        <Suspense fallback={<PageEmptyState title="Loading insights…" data-testid="tasks-panel-loading" />}>
          <div data-testid="tasks-panel-insights">
            {/*
              P-319 · "Insights/Timeline rows that open items behave the same". Both panels fall
              back to navigating to the board when they are given no handler, which is exactly the
              page change the card forbids; the page's own handler is therefore passed in.
            */}
            <InsightsPanel
              tab={panelTabOf(tab, panelSubject)}
              source={insightsSource}
              onOpenItem={openItemCard}
            />
          </div>
        </Suspense>
      ) : tab === 'timeline' ? (
        <Suspense fallback={<PageEmptyState title="Loading the timeline…" data-testid="tasks-panel-loading" />}>
          <div data-testid="tasks-panel-timeline">
            <TimelinePanel
              tab={panelTabOf(tab, panelSubject)}
              source={insightsSource}
              onOpenItem={openItemCard}
            />
          </div>
        </Suspense>
      ) : error !== null ? (
        <PageErrorState kind="error" title="Tasks could not be loaded" hint={error} data-testid="tasks-error" />
      ) : !loading && rows.length === 0 ? (
        <TasksEmptyState tab={listTab} filtered={activeFilterCount(filters) > 0} />
      ) : (
        <div className="ez-tasks__groups" ref={listRef} style={columnStyle} data-testid="tasks-groups">
          {/*
            P-270 · one header for the whole list, sticky, above every bucket. The buckets keep
            their own counts; they are groupings of these six columns, not columns of their own.
            P-285 · its checkbox column is now **Select all**, tri-state over every loaded row.
          */}
          <TaskListHeader
            tab={listTab}
            sort={sort}
            onSort={setSort}
            selectState={headerState}
            onSelectAll={(checked) => (checked ? selectAllLoaded() : clearSelection())}
            /* P-305 · desktop and tablet only; a phone draws no strip and no gutters. */
            resizable={!phone}
            resizedColumns={resizedColumns}
            onResizeStart={resize.start}
            onResize={(key, width) =>
              commitColumnWidths(withTaskColumnWidth(columnWidths, listTab, key, width))
            }
            onResetWidth={(key) => commitColumnWidths(withoutTaskColumnWidth(columnWidths, listTab, key))}
          />
          {groups.map((group) => {
            const isCollapsed = collapsed[group.key] === true;
            const groupIds = group.rows.map((row) => row.id);
            /** This group's own cursor — `null`/absent means the group is fully loaded (P-318). */
            const groupCursor = cursors[group.key];
            return (
              <section
                key={group.key}
                className="ez-tasks__group"
                data-testid={`tasks-group-${group.key}`}
              >
                <div className="ez-tasks__group-head">
                  <button
                    type="button"
                    className="ez-tasks__group-toggle"
                    aria-expanded={!isCollapsed}
                    aria-label={`Collapse ${group.label}`}
                    data-testid={`tasks-group-toggle-${group.key}`}
                    onClick={() => setCollapsed((current) => ({ ...current, [group.key]: !isCollapsed }))}
                  >
                    {isCollapsed ? '▸' : '▾'}
                  </button>
                  {/*
                    P-285 · the bucket's own box, tri-state over the rows of **this** bucket that
                    are loaded. It sits between the chevron and the name, which is where the grid
                    keeps it (`GroupHeader`'s checkbox gutter, `group-select-<id>`).
                  */}
                  <TaskSelectBox
                    className="ez-tasks__group-select"
                    state={taskSelectState(selection, groupIds)}
                    label={`Select all in ${group.label}`}
                    testId={`tasks-group-select-${group.key}`}
                    onChange={(checked) => {
                      setSelectMode(true);
                      setSelection((current) =>
                        setTaskGroupSelected(current, loadedIds, groupIds, checked),
                      );
                    }}
                  />
                  <span className="ez-tasks__group-label">{group.label}</span>
                  <span className="ez-tasks__group-count" data-testid={`tasks-group-count-${group.key}`}>
                    {group.count}
                  </span>
                </div>
                {isCollapsed ? null : (
                  <div className="ez-tasks__rows">
                    {group.rows.map((row) => (
                      <TaskRow
                        key={row.id}
                        row={row}
                        tab={listTab}
                        selected={chosen.has(row.id)}
                        onToggleSelected={(modifiers) => toggleRow(row.id, modifiers)}
                        onOpenItem={() => openItemCard(row)}
                        /* P-319 · the row the open card is about stays marked. */
                        open={openItemId !== null && row.itemId === openItemId}
                        onEditStatus={() => openEditor(row, 'status')}
                        onEditDue={() => openEditor(row, 'due')}
                        onNudge={() => setNudging(row)}
                        onReassign={() => openEditor(row, 'assignee')}
                        /*
                         * P-301 §3 · one row, through exactly the same `bulk.ts` path the bar
                         * uses — so the toast, the Undo, the reload and the "not yours" answer
                         * are one implementation and a row action cannot drift from the bulk one.
                         */
                        onArchive={(archive) => {
                          void archiveTasksBulk(source, [row], archive ? 'archive' : 'unarchive')
                            .then((outcome) =>
                              afterBulk(
                                outcome,
                                archive ? 'Archive' : 'Unarchive',
                                archive ? 'archived' : 'unarchived',
                              ),
                            )
                            .catch(bulkFailed);
                        }}
                        editor={editor?.row.id === row.id ? editorNode : undefined}
                        editorRole={editor?.row.id === row.id ? editor.role : null}
                        onLongPress={
                          phone
                            ? () => {
                                setSelectMode(true);
                                setSelection((current) =>
                                  current.ids.includes(row.id)
                                    ? current
                                    : toggleTask(current, order, row.id),
                                );
                              }
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )}
                {/*
                  **P-318 · this group's own "Show more".**

                  The list used to carry one at the foot of the page, because the cursor was the
                  tab's: pressing it filled whichever group the global order had reached, and the
                  groups below it stayed empty under headers that counted their rows. The control
                  belongs to the group it grows, which is where the board grid keeps it too
                  (`board-view/table/store.ts#loadMore`, a cursor per group).

                  A collapsed group draws none: there is nothing on screen for a page to join.
                */}
                {isCollapsed || groupCursor === null || groupCursor === undefined ? null : (
                  <div className="ez-tasks__group-more">
                    <span
                      className="ez-tasks__group-more-note"
                      data-testid={`tasks-group-more-note-${group.key}`}
                    >
                      Showing {group.rows.length} of {group.count}
                    </span>
                    <button
                      type="button"
                      className="ez-tasks__group-more-button"
                      data-testid={`tasks-load-more-${group.key}`}
                      aria-label={`Show more in ${group.label}`}
                      disabled={loading || loadingGroups[group.key] === true}
                      onClick={() => loadMoreGroup(group.key)}
                    >
                      {loadingGroups[group.key] === true ? 'Loading…' : 'Show more'}
                    </button>
                  </div>
                )}
              </section>
            );
          })}
          {/*
            The footer is now **only** the count, summed over the whole tab (P-318): every "Show
            more" lives under the group it grows. It stays once the list has ever paged, so the
            last turn ends on *"Showing 638 of 638"* rather than on a row count that vanishes —
            which is the only way somebody can tell the list finished rather than broke (P-277).
          */}
          {tabTotal > rows.length || rows.length > TASK_PAGE_SIZE ? (
            <div className="ez-tasks__more">
              <span className="ez-tasks__more-note" data-testid="tasks-more-note">
                Showing {rows.length} of {tabTotal}
              </span>
            </div>
          ) : null}
        </div>
      )}

      {isList && selectedRows.length > 0 ? (
        <div className="ez-tasks__bulk" role="region" aria-label="Bulk actions" data-testid="tasks-bulk-bar">
          <span className="ez-tasks__bulk-count" data-testid="tasks-bulk-count">
            {describeTaskSelection(selectedRows.length)}
          </span>
          {/*
            P-285 · the other half of "Select all", and the reason the header box selects only
            what is loaded: when the tab is longer than the pages on screen, the bar says so and
            offers the whole tab **explicitly**, with the number in the sentence. A header box
            that silently reached rows nobody has seen would make this count a lie.
          */}
          {hasMorePages ? (
            <>
              <span className="ez-tasks__bulk-sep" aria-hidden="true">
                ·
              </span>
              <button
                type="button"
                className="ez-tasks__bulk-all"
                data-testid="tasks-select-all-tab"
                disabled={selectingTab}
                onClick={selectWholeTab}
              >
                {selectingTab ? 'Selecting…' : `Select all ${tabTotal} in this tab`}
              </button>
            </>
          ) : null}
          <button
            type="button"
            data-testid="tasks-bulk-done"
            onClick={() => {
              void markTasksDone(source, selectedRows)
                .then((outcome) => afterBulk(outcome, 'Mark done'))
                .catch(bulkFailed);
            }}
          >
            Mark done
          </button>
          {/*
            **P-301 §2 · Set status ▾.** A `<select>` and not a popover, for the same reason the
            toolbar's group-by and sort are: it is the one control a phone draws its own native
            wheel for, so the 44 px target and the "no zoom" rule come free, and there is one
            implementation for both widths rather than a menu that has to be re-tested on a
            finger.

            What it offers is resolved from the **boards**, never from the rows (`bulk.ts`): on
            one board, that board's own labels in its own order; across boards, only the labels
            whose *text* exists on every one of them, because a label id means different things
            on different boards and that is precisely the confusion P-294 was filed about. A done
            label is marked, so "which of these finishes the task" is answerable without opening
            each board.
          */}
          <label className="ez-tasks__bulk-status">
            Status
            <select
              data-testid="tasks-bulk-status"
              value=""
              disabled={statusChoices === null || statusChoices.length === 0}
              onChange={(event) => {
                const text = event.target.value;
                event.target.value = '';
                if (text === '') return;
                void setTasksStatus(source, selectedRows, text)
                  .then((outcome) => afterBulk(outcome, 'Set status'))
                  .catch(bulkFailed);
              }}
            >
              <option value="">
                {statusChoices === null
                  ? 'Loading…'
                  : statusChoices.length === 0
                    ? 'No shared label'
                    : 'Set status…'}
              </option>
              {(statusChoices ?? []).map((choice) => (
                <option key={choice.text} value={choice.text}>
                  {choice.isDone ? `${choice.text} \u2713` : choice.text}
                </option>
              ))}
            </select>
          </label>
          <label className="ez-tasks__bulk-due">
            Due
            <input
              type="date"
              data-testid="tasks-bulk-due"
              value={bulkDue}
              onChange={(event) => {
                const next = event.target.value;
                setBulkDue(next);
                if (next === '') return;
                void setTasksDue(source, selectedRows, next)
                  .then((outcome) => {
                    setBulkDue('');
                    afterBulk(outcome, 'Change due');
                  })
                  .catch(() => {
                    setBulkDue('');
                    bulkFailed();
                  });
              }}
            />
          </label>
          {/*
            **P-301 §3 · Archive / Unarchive.** Which one is offered follows the tab, because
            there is exactly one thing a person means on each: in My Tasks and Tasks Assigned,
            "take this off my list"; in the Archive, "put it back". Offering both everywhere
            would be two buttons of which one is always a no-op.
          */}
          {listTab === 'archive' ? (
            <button
              type="button"
              data-testid="tasks-bulk-unarchive"
              onClick={() => {
                void archiveTasksBulk(source, selectedRows, 'unarchive')
                  .then((outcome) => afterBulk(outcome, 'Unarchive', 'unarchived'))
                  .catch(bulkFailed);
              }}
            >
              Unarchive
            </button>
          ) : (
            <button
              type="button"
              data-testid="tasks-bulk-archive"
              onClick={() => {
                void archiveTasksBulk(source, selectedRows, 'archive')
                  .then((outcome) => afterBulk(outcome, 'Archive', 'archived'))
                  .catch(bulkFailed);
              }}
            >
              Archive
            </button>
          )}
          <button type="button" data-testid="tasks-bulk-clear" onClick={clearSelection}>
            Deselect all
          </button>
        </div>
      ) : null}

      {settingsOpen && settings ? (
        <TaskSettingsPanel
          settings={settings}
          onChange={saveSettings}
          onClose={() => setSettingsOpen(false)}
          mapping={mappingPanel}
        />
      ) : null}

      {nudging ? (
        <NudgeDialog
          row={nudging}
          onClose={() => setNudging(null)}
          onSend={async (message) => {
            const row = nudging;
            try {
              await source.nudge({
                itemId: row.itemId,
                assigneeId: row.assignee?.id ?? '',
                ...(message === '' ? {} : { message }),
              });
              toast(`Nudged ${row.assignee?.name ?? 'the assignee'}.`, { kind: 'positive' });
              setNudging(null);
            } catch (cause) {
              toast(cause instanceof Error ? cause.message : 'The nudge could not be sent.', { kind: 'negative' });
            }
          }}
        />
      ) : null}
    </div>
    {cardNode}
    </>
  );
}

/* ── the toolbar ──────────────────────────────────────────────────────────── */

interface TasksToolbarProps {
  listTab: TaskTab;
  groupBy: TaskGroupBy;
  sort: TaskSort;
  filters: TaskFilterState;
  savedViews: TaskSavedView[];
  filtersOpen: boolean;
  /** P-285 · so the button can say whether the list is already all in. */
  selectState: TaskSelectState;
  onSelectAll(): void;
  onToggleFilters(): void;
  onGroupBy(next: TaskGroupBy): void;
  onSort(next: TaskSort): void;
  onQuery(next: string): void;
  onApplyView(view: TaskSavedView): void;
  onDeleteView(id: string): void;
}

function TasksToolbar({
  listTab,
  groupBy,
  sort,
  filters,
  savedViews,
  filtersOpen,
  selectState,
  onSelectAll,
  onToggleFilters,
  onGroupBy,
  onSort,
  onQuery,
  onApplyView,
  onDeleteView,
}: TasksToolbarProps) {
  const active = activeFilterCount(filters);
  const viewsForTab = savedViews.filter((view) => view.tab === listTab);
  const searchRef = useRef<HTMLInputElement>(null);

  return (
    <div className="ez-tasks__toolbar" data-testid="tasks-toolbar">
      <input
        ref={searchRef}
        type="search"
        className="ez-tasks__search"
        placeholder="Search tasks"
        aria-label="Search tasks"
        data-testid="tasks-search"
        value={filters.query}
        onChange={(event) => onQuery(event.target.value)}
      />

      {/*
        Round T4 · on a phone the four controls beside the search box become **one horizontally
        scrolling row of chips** (F9-D32). On a desktop this wrapper is `display: contents`, so
        the toolbar is the same flat 10 px-gapped line My Work measured and nothing moved.
      */}
      <div className="ez-tasks__chips" data-testid="tasks-toolbar-chips">
        <label className="ez-tasks__toolbar-field">
          <span>Group by</span>
          <select
            data-testid="tasks-groupby"
            value={groupBy}
            onChange={(event) => onGroupBy(event.target.value as TaskGroupBy)}
          >
            {TASK_GROUP_BY_FOR_TAB[listTab].map((option) => (
              <option key={option} value={option} data-testid={`tasks-groupby-${option}`}>
                {TASK_GROUP_BY_LABELS[option]}
              </option>
            ))}
          </select>
        </label>

        <label className="ez-tasks__toolbar-field">
          <span>Sort</span>
          <select
            data-testid="tasks-sort"
            value={sort}
            onChange={(event) => onSort(event.target.value as TaskSort)}
          >
            {TASK_SORTS.map((option) => (
              <option key={option} value={option} data-testid={`tasks-sort-${option}`}>
                {TASK_SORT_LABELS[option]}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="ez-tasks__filter-toggle"
          aria-expanded={filtersOpen}
          data-testid="tasks-filter-toggle"
          onClick={onToggleFilters}
        >
          {active > 0 ? `Filter (${active})` : 'Filter'}
        </button>

        {/*
          P-285 · the user asked for *"a button Select all"*, and this is it — beside the filters,
          where a person looking for it can see it without knowing that the strip above the first
          row has a checkbox in it. It selects every **loaded** row, exactly as the header box
          and `Ctrl/Cmd+A` do; the bulk bar then offers the rest of the tab if there is any.
          On a phone it is hidden until a long press turns select mode on (F9-D34).
        */}
        <button
          type="button"
          className="ez-tasks__select-all-button"
          data-selected={selectState === 'all' ? '' : undefined}
          data-testid="tasks-select-all-button"
          onClick={onSelectAll}
        >
          Select all
        </button>

        <label className="ez-tasks__toolbar-field">
          <span>Views</span>
          <select
            data-testid="tasks-saved-views"
            value=""
            onChange={(event) => {
              const view = viewsForTab.find((entry) => entry.id === event.target.value);
              if (view) onApplyView(view);
            }}
          >
            <option value="">{viewsForTab.length === 0 ? 'No saved views' : 'Saved views'}</option>
            {viewsForTab.map((view) => (
              <option key={view.id} value={view.id} data-testid={`tasks-saved-view-${view.id}`}>
                {view.name}
              </option>
            ))}
          </select>
        </label>

        {viewsForTab.length > 0 ? (
          <button
            type="button"
            className="ez-tasks__view-delete"
            data-testid="tasks-saved-view-delete"
            onClick={() => {
              const last = viewsForTab[viewsForTab.length - 1];
              if (last) onDeleteView(last.id);
            }}
          >
            Delete the last view
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ── the empty states ─────────────────────────────────────────────────────── */

function TasksEmptyState({ tab, filtered }: { tab: TaskTab; filtered: boolean }) {
  if (filtered) {
    return (
      <PageEmptyState
        title="No task matches those filters"
        hint="Clear a filter, or widen the due range."
        data-testid="tasks-empty"
      />
    );
  }
  if (tab === 'assigned') {
    return (
      <PageEmptyState
        title="You have not assigned anything yet"
        hint="Put somebody in a people column on any board and the delegation shows up here — with a nudge, a reassign and the date it was given."
        data-testid="tasks-empty"
      />
    );
  }
  if (tab === 'archive') {
    return (
      <PageEmptyState
        title="Nothing has been archived yet"
        hint="A task moves here once it has been done for longer than your archive delay. Reopening it takes it back out on its own."
        data-testid="tasks-empty"
      />
    );
  }
  return (
    <PageEmptyState
      title="Nothing is assigned to you"
      hint="Every item and subitem that names you in a people column — on a board you can open — shows up here, grouped by when it is due."
      data-testid="tasks-empty"
    />
  );
}
